import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { AiOutlineSearch } from 'react-icons/ai';
import { BsChatLeftText, BsExclamationTriangle, BsPeople, BsPencil, BsPlusLg, BsToggleOff, BsToggleOn } from 'react-icons/bs';
import { FaRoute } from 'react-icons/fa';
import { IoCloseCircle } from 'react-icons/io5';
import { Menu } from '../../components/Menu/Menu';
import LoadingWindow from '../../components/LoadingWindow';
import { automaticCloseAlert, confirmCloseAlert } from '../../functions/alerts';
import { getValueCookie } from '../../services/cookieService';
import {
    ROUTE_AUTH_STATUS,
    authorizeRoute,
    formatMoney,
    getCurrentCompanyId,
    isRouteCounted,
    listRoutes,
    loadCatalogs,
    loadCustomerGoals,
    salespersonLabel,
    setRouteActive,
    subscribeToRouteChanges,
    summarizeCountedRoutes,
    summarizeCustomerGoals,
} from '../../services/salesRoutesService';
import { ChangeSalespersonDialog, RejectRouteDialog, RejectionCommentDialog } from './RouteDialogs';
import RouteEditor from './RouteEditor';
import { ReviewActions, ReviewBadge, RouteCustomersToggle, RouteReviewMeta, customerCount } from './RouteParts';
import SalespersonSummary from './SalespersonSummary';
import styles from '../../css/SalesRoutes.module.css';

/**
 * SalesRoutes — los usuarios de nivel 1 arman rutas (lista de clientes con nombre y vendedor)
 * y se las asignan a los vendedores; cada cambio pasa a revisión de un super admin.
 * Con reviewMode es la pantalla de autorización (ver SalesRoutesAuthorization.js): prioriza
 * consultar y decidir, y lo que se guarda ahí queda autorizado.
 * Demo para autorización del cliente (2026-11-03): las rutas viven en salesRoutesService.js
 * (mock); buscar "TODO: BACKEND" para conectarlas.
 */

const SOURCE_LABELS = { real: 'live data', mock: 'sample data', unavailable: 'unavailable' };

const REVIEW_MESSAGE_MS = 5000;

const notifySaved = (route, savedText) => {
    if (route.authStatus !== ROUTE_AUTH_STATUS.pending) {
        automaticCloseAlert('correct', savedText);
        return;
    }
    Swal.fire({
        icon: 'info',
        title: 'Sent for authorization',
        text: `"${route.name}" is now Pending to review. A super admin will authorize or reject it.`,
        timer: REVIEW_MESSAGE_MS,
        timerProgressBar: true,
        confirmButtonText: 'OK',
    });
};

export function SalesRoutesScreen({ reviewMode }) {
    const idCompany = useMemo(getCurrentCompanyId, []);
    const loadingRef = useRef(null);

    const [catalogs, setCatalogs] = useState(null);
    const [routes, setRoutes] = useState([]);
    const [goals, setGoals] = useState(new Map());
    const [goalsSource, setGoalsSource] = useState('unavailable');
    const [loadError, setLoadError] = useState('');

    // Autorización abre en el resumen por vendedor: ahí lo principal es consultar.
    const [view, setView] = useState(reviewMode ? 'salespeople' : 'routes');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [reviewFilter, setReviewFilter] = useState('all');
    const [salespersonFilter, setSalespersonFilter] = useState('');

    // null = lista; { route: null } = ruta nueva; { route } = edición.
    const [editing, setEditing] = useState(null);
    const [reassigning, setReassigning] = useState(null);
    const [rejecting, setRejecting] = useState(null);
    const [viewingComment, setViewingComment] = useState(null);
    const [togglingId, setTogglingId] = useState(null);
    const [authorizingId, setAuthorizingId] = useState(null);

    const load = useCallback(async () => {
        loadingRef.current.showState(true);
        setLoadError('');
        let loadedCatalogs = null;
        let loadedRoutes = [];
        let loadedGoals = { goals: new Map(), source: 'unavailable' };
        let failed = false;
        try {
            loadedCatalogs = await loadCatalogs(idCompany);
            // Sin metas la pantalla sigue siendo útil: los totales simplemente no se muestran.
            const [routesResult, goalsResult] = await Promise.all([
                listRoutes(idCompany),
                loadCustomerGoals(idCompany, loadedCatalogs.customers).catch(() => loadedGoals),
            ]);
            loadedRoutes = routesResult;
            loadedGoals = goalsResult;
        } catch (e) {
            failed = true;
        }
        // Sin ref = el usuario ya salió de la pantalla mientras cargaba.
        if (!loadingRef.current) return;
        if (failed) {
            setLoadError('Could not load the routes. Try again please.');
        } else {
            setCatalogs(loadedCatalogs);
            setRoutes(loadedRoutes);
            setGoals(loadedGoals.goals);
            setGoalsSource(loadedGoals.source);
        }
        loadingRef.current.showState(false);
    }, [idCompany]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => subscribeToRouteChanges(idCompany, async () => {
        const updatedRoutes = await listRoutes(idCompany);
        if (loadingRef.current) setRoutes(updatedRoutes);
    }), [idCompany]);

    const salespeople = catalogs ? catalogs.salespeople : [];

    const salespersonName = useCallback((no) => (
        salespersonLabel(salespeople.find((s) => s.salespersonNo === no)) || no
    ), [salespeople]);

    const replaceRoute = (updated) => setRoutes((current) => current.map((r) => (r.id === updated.id ? updated : r)));

    const toggleActive = async (route) => {
        setTogglingId(route.id);
        try {
            replaceRoute(await setRouteActive(idCompany, route.id, !route.active));
        } catch (e) {
            confirmCloseAlert('incorrect', e.message);
        }
        setTogglingId(null);
    };

    const authorize = async (route) => {
        setAuthorizingId(route.id);
        try {
            replaceRoute(await authorizeRoute(idCompany, route.id));
            automaticCloseAlert('correct', 'Route authorized');
        } catch (e) {
            confirmCloseAlert('incorrect', e.message);
        }
        setAuthorizingId(null);
    };

    const onRejected = (updated) => {
        replaceRoute(updated);
        setRejecting(null);
        automaticCloseAlert('correct', 'Route rejected');
    };

    const onEditorSaved = (saved, isNew) => {
        setRoutes((current) => (isNew ? [...current, saved] : current.map((r) => (r.id === saved.id ? saved : r))));
        setEditing(null);
        notifySaved(saved, isNew ? 'Route created' : 'Route saved');
    };

    const onSalespersonChanged = (updated) => {
        replaceRoute(updated);
        setReassigning(null);
        notifySaved(updated, 'Route saved');
    };

    const closeReassign = useCallback(() => setReassigning(null), []);
    const closeReject = useCallback(() => setRejecting(null), []);
    const closeComment = useCallback(() => setViewingComment(null), []);

    // Incluye el vendedor filtrado aunque ya no tenga rutas, para que el select no quede en blanco.
    const assignedSalespeople = useMemo(() => {
        const nos = new Set(routes.map((r) => r.salespersonNo));
        if (salespersonFilter) nos.add(salespersonFilter);
        return Array.from(nos)
            .map((no) => ({ no, label: salespersonName(no) }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    }, [routes, salespersonFilter, salespersonName]);

    const visibleRoutes = useMemo(() => {
        const q = search.trim().toLowerCase();
        return routes
            .filter((r) => statusFilter === 'all' || (statusFilter === 'active') === r.active)
            .filter((r) => reviewFilter === 'all' || r.authStatus === reviewFilter)
            .filter((r) => !salespersonFilter || r.salespersonNo === salespersonFilter)
            .filter((r) => !q || `${r.name} ${salespersonName(r.salespersonNo)}`.toLowerCase().includes(q))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
    }, [routes, statusFilter, reviewFilter, salespersonFilter, search, salespersonName]);

    const countedRoutes = routes.filter(isRouteCounted).length;
    const countedTotals = useMemo(() => summarizeCountedRoutes(routes, goals), [routes, goals]);
    const countByStatus = (status) => routes.filter((r) => r.authStatus === status).length;

    const hasFilters = Boolean(search.trim() || statusFilter !== 'all' || reviewFilter !== 'all' || salespersonFilter);
    const clearFilters = () => { setSearch(''); setStatusFilter('all'); setReviewFilter('all'); setSalespersonFilter(''); };

    const showReviewStatus = (status) => {
        clearFilters();
        setReviewFilter(status);
        setView('routes');
    };

    const renderDemoNotice = () => {
        const usesMock = [catalogs.customersSource, catalogs.salespeopleSource, goalsSource].includes('mock');
        return (
            <div className={`${styles.notice} ${usesMock ? styles.noticeMock : ''}`} role="status">
                <BsExclamationTriangle className={styles.noticeIcon} aria-hidden="true" />
                <div>
                    <strong>Demo:</strong> routes are saved in this browser only.
                    {' '}Customers: <strong>{SOURCE_LABELS[catalogs.customersSource]}</strong> ({catalogs.customers.length.toLocaleString()}).
                    {' '}Salespeople: <strong>{SOURCE_LABELS[catalogs.salespeopleSource]}</strong> ({salespeople.length}).
                    {' '}Goals: <strong>{SOURCE_LABELS[goalsSource]}</strong> ({goals.size.toLocaleString()}).
                </div>
            </div>
        );
    };

    const renderRouteCard = (r) => {
        const routeGoals = summarizeCustomerGoals(r.customers, goals);
        return (
            <li key={r.id} className={`${styles.routeCard} ${r.active ? '' : styles.routeCardInactive}`}>
                <div className={styles.routeHead}>
                    <h2 className={styles.routeName}>{r.name}</h2>
                    <div className={styles.routeBadges}>
                        <ReviewBadge status={r.authStatus} />
                        <span className={`${styles.badge} ${r.active ? styles.badgeActive : styles.badgeInactive}`}>{r.active ? 'Active' : 'Inactive'}</span>
                    </div>
                </div>
                <dl className={styles.routeFacts}>
                    <div className={styles.factWide}><dt>Salesperson</dt><dd>{salespersonName(r.salespersonNo)}</dd></div>
                    <div><dt>Customers</dt><dd>{customerCount(r.customers.length)}</dd></div>
                    <div>
                        <dt>Expected sales</dt>
                        {routeGoals.withGoal > 0 ? (
                            <>
                                <dd className={styles.factAmount}>{formatMoney(routeGoals.expected)}</dd>
                                <dd className={styles.factNote}>{routeGoals.withGoal} of {r.customers.length} with goal</dd>
                            </>
                        ) : <dd className={styles.factNote}>No goals</dd>}
                        {!isRouteCounted(r) && <dd className={styles.factNote}>Not counted in totals</dd>}
                    </div>
                </dl>
                {reviewMode && <RouteReviewMeta route={r} />}
                {reviewMode && <RouteCustomersToggle route={r} goals={goals} />}
                {!reviewMode && r.authStatus === ROUTE_AUTH_STATUS.rejected && (
                    <button type="button" className={`${styles.linkBtn} ${styles.linkBtnDanger}`} onClick={() => setViewingComment(r)}>
                        <BsChatLeftText aria-hidden="true" /> View rejection comment
                    </button>
                )}
                {reviewMode && <ReviewActions route={r} busy={authorizingId !== null} onAuthorize={authorize} onReject={setRejecting} />}
                <div className={styles.routeActions}>
                    <button type="button" className={styles.btnSecondary} onClick={() => setEditing({ route: r })}><BsPencil aria-hidden="true" /> Edit</button>
                    <button type="button" className={styles.btnSecondary} onClick={() => setReassigning(r)}><BsPeople aria-hidden="true" /> Change salesperson</button>
                    <button type="button" className={styles.btnSecondary} disabled={togglingId !== null} onClick={() => toggleActive(r)}>
                        {r.active ? <BsToggleOff aria-hidden="true" /> : <BsToggleOn aria-hidden="true" />} {r.active ? 'Deactivate' : 'Activate'}
                    </button>
                </div>
            </li>
        );
    };

    const renderRoutes = () => (
        <>
            <div className={styles.toolbar}>
                <div className={styles.searchBox}>
                    <AiOutlineSearch className={styles.searchIcon} aria-hidden="true" />
                    <input
                        className={styles.searchInput}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Route or salesperson…"
                        aria-label="Search routes"
                    />
                    {search && (
                        <button type="button" className={styles.searchClear} onClick={() => setSearch('')} aria-label="Clear search">
                            <IoCloseCircle aria-hidden="true" />
                        </button>
                    )}
                </div>
                <select className={styles.input} value={reviewFilter} onChange={(e) => setReviewFilter(e.target.value)} aria-label="Filter by authorization">
                    <option value="all">All authorizations</option>
                    <option value={ROUTE_AUTH_STATUS.pending}>Pending to review</option>
                    <option value={ROUTE_AUTH_STATUS.authorized}>Authorized</option>
                    <option value={ROUTE_AUTH_STATUS.rejected}>Rejected</option>
                </select>
                <select className={styles.input} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
                <select className={styles.input} value={salespersonFilter} onChange={(e) => setSalespersonFilter(e.target.value)} aria-label="Filter by salesperson">
                    <option value="">All salespeople</option>
                    {assignedSalespeople.map((s) => <option key={s.no} value={s.no}>{s.label}</option>)}
                </select>
            </div>

            {routes.length === 0 && (
                <div className={styles.empty}>
                    <p>There are no routes yet.</p>
                    <button type="button" className={styles.btnPrimary} onClick={() => setEditing({ route: null })}><BsPlusLg aria-hidden="true" /> New route</button>
                </div>
            )}
            {routes.length > 0 && visibleRoutes.length === 0 && (
                <div className={styles.empty}>
                    <p>No routes match these filters.</p>
                    {hasFilters && <button type="button" className={styles.btnSecondary} onClick={clearFilters}>Clear filters</button>}
                </div>
            )}

            <ul className={styles.routeGrid}>{visibleRoutes.map(renderRouteCard)}</ul>
        </>
    );

    const renderReviewKpi = (status, label, className) => (
        <button
            type="button"
            className={`${styles.kpi} ${styles.kpiButton} ${className} ${view === 'routes' && reviewFilter === status ? styles.kpiSelected : ''}`}
            aria-pressed={view === 'routes' && reviewFilter === status}
            onClick={() => showReviewStatus(status)}
        >
            <span className={styles.kpiLabel}>{label}</span>
            <span className={styles.kpiValue}>{countByStatus(status)}</span>
        </button>
    );

    const renderKpis = () => (
        <div className={styles.kpis}>
            {reviewMode ? (
                <>
                    {renderReviewKpi(ROUTE_AUTH_STATUS.pending, 'Pending to review', styles.kpiPending)}
                    {renderReviewKpi(ROUTE_AUTH_STATUS.authorized, 'Authorized', styles.kpiAuthorized)}
                    {renderReviewKpi(ROUTE_AUTH_STATUS.rejected, 'Rejected', styles.kpiRejected)}
                </>
            ) : (
                <>
                    <div className={styles.kpi}><span className={styles.kpiLabel}>Routes</span><span className={styles.kpiValue}>{routes.length}</span></div>
                    <div className={styles.kpi}><span className={styles.kpiLabel}>Active and authorized</span><span className={styles.kpiValue}>{countedRoutes}</span></div>
                    <div className={styles.kpi}><span className={styles.kpiLabel}>Customers on those routes</span><span className={styles.kpiValue}>{countedTotals.customers.toLocaleString()}</span></div>
                </>
            )}
            <div className={styles.kpi}><span className={styles.kpiLabel}>Expected sales, authorized</span><span className={styles.kpiValue}>{formatMoney(countedTotals.expected)}</span></div>
        </div>
    );

    const renderViewTab = (key, label) => (
        <button type="button" role="tab" aria-selected={view === key} className={`${styles.tab} ${view === key ? styles.tabActive : ''}`} onClick={() => setView(key)}>
            {label}
        </button>
    );

    const renderList = () => {
        const routesTab = renderViewTab('routes', 'Routes');
        const salespeopleTab = renderViewTab('salespeople', 'By salesperson');
        return (
            <>
                {renderKpis()}
                <div className={styles.tabs} role="tablist" aria-label="Route views">
                    {reviewMode ? salespeopleTab : routesTab}
                    {reviewMode ? routesTab : salespeopleTab}
                </div>
                {view === 'routes' ? renderRoutes() : (
                    <SalespersonSummary
                        routes={routes}
                        goals={goals}
                        salespersonName={salespersonName}
                        reviewMode={reviewMode}
                        reviewBusy={authorizingId !== null}
                        onAuthorize={authorize}
                        onReject={setRejecting}
                        onViewComment={setViewingComment}
                    />
                )}
            </>
        );
    };

    const listTitle = reviewMode ? 'Route Authorization' : 'Route Builder';
    const listSubtitle = reviewMode ? 'Review routes and authorize or reject them' : 'Build routes and assign them to salespeople';
    const heroSubtitle = editing
        ? (editing.route ? `Editing ${editing.route.name}` : 'Name it, assign a salesperson and add customers')
        : listSubtitle;

    return (
        <div className={styles.page}>
            <Menu />
            <LoadingWindow ref={loadingRef} />

            <div className={styles.hero}>
                <div className={styles.heroLeft}>
                    <div className={styles.heroIconBox}><FaRoute className={styles.heroIcon} aria-hidden="true" /></div>
                    <div className={styles.heroText}>
                        <h1 className={styles.heroTitle}>{editing ? (editing.route ? 'Edit route' : 'New route') : listTitle}</h1>
                        <p className={styles.heroSubtitle}>{heroSubtitle}</p>
                    </div>
                </div>
                {catalogs && !editing && (
                    <button type="button" className={`${styles.heroBtn} ${reviewMode ? styles.heroBtnQuiet : ''}`} onClick={() => setEditing({ route: null })}>
                        <BsPlusLg aria-hidden="true" /> New route
                    </button>
                )}
            </div>

            <div className={styles.body}>
                {loadError && (
                    <div className={styles.empty} role="alert">
                        <p>{loadError}</p>
                        <button type="button" className={styles.btnPrimary} onClick={load}>Try again</button>
                    </div>
                )}
                {catalogs && !loadError && (
                    <>
                        {renderDemoNotice()}
                        {editing ? (
                            <RouteEditor
                                key={editing.route ? editing.route.id : 'new'}
                                idCompany={idCompany}
                                route={editing.route}
                                customers={catalogs.customers}
                                salespeople={salespeople}
                                routes={routes}
                                goals={goals}
                                autoAuthorize={Boolean(reviewMode)}
                                onCancel={() => setEditing(null)}
                                onSaved={onEditorSaved}
                            />
                        ) : renderList()}
                    </>
                )}
            </div>

            {reassigning && (
                <ChangeSalespersonDialog
                    idCompany={idCompany}
                    route={reassigning}
                    salespeople={salespeople}
                    autoAuthorize={Boolean(reviewMode)}
                    onClose={closeReassign}
                    onSaved={onSalespersonChanged}
                />
            )}
            {rejecting && <RejectRouteDialog idCompany={idCompany} route={rejecting} onClose={closeReject} onSaved={onRejected} />}
            {viewingComment && <RejectionCommentDialog route={viewingComment} onClose={closeComment} />}
        </div>
    );
}

export default function SalesRoutes() {
    if (getValueCookie('TypeUser') !== '1') {
        return (
            <div className={styles.page}>
                <Menu />
                <div className={styles.denied}>This view is only available to administrators.</div>
            </div>
        );
    }
    return <SalesRoutesScreen />;
}
